import { useMemo, useState } from 'react';
import { evidenceCards, feedRows } from '@/mocks/gossip';
import SectionHead from './SectionHead';

type Filter = '全部' | '实锤' | '截图' | '录音' | '视频' | '线索';

const filters: Filter[] = ['全部', '实锤', '截图', '录音', '视频', '线索'];

export default function SearchSection() {
  const [kw, setKw] = useState('');
  const [filter, setFilter] = useState<Filter>('全部');

  const rows = useMemo(() => {
    const pool = [...evidenceCards, ...feedRows];
    let list = pool.map((c) => ({
      tag: c.tag,
      title: c.title,
      category: 'category' in c ? c.category : c.category,
      views: 'views' in c ? c.views : undefined,
      time: 'time' in c ? c.time : c.date,
    }));
    if (filter !== '全部') {
      list = list.filter((r) => r.tag.includes(filter) || r.category.includes(filter));
    }
    if (kw.trim()) {
      list = list.filter((r) => r.title.includes(kw.trim()) || r.category.includes(kw.trim()));
    }
    return list.slice(0, 8);
  }, [kw, filter]);

  return (
    <section id="zhengju" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <SectionHead
          en="Evidence Search"
          title="证据检索 · 按类别与关键词快速定位"
          desc="支持按实锤状态与素材类型筛选，也能输入关键词在所有已归档证据与线索中检索。"
        />

        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="inline-flex w-full flex-wrap items-center gap-1 rounded-full bg-secondary-100 p-1 lg:w-auto">
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`flex-1 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors lg:flex-none ${
                    filter === f
                      ? 'bg-accent-500 text-background-50'
                      : 'text-foreground-700 hover:bg-background-50'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            <label className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                placeholder="输入关键词，如：时间线 / 声纹 / 原图"
                className="w-full min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {kw && (
                <button
                  type="button"
                  onClick={() => setKw('')}
                  aria-label="清空搜索"
                  className="text-foreground-400 hover:text-primary-600"
                >
                  <i className="ri-close-circle-line text-base" />
                </button>
              )}
            </label>
          </div>

          <ul className="mt-5 divide-y divide-background-100">
            {rows.map((r) => (
              <li key={r.title}>
                <a
                  href="#shichui"
                  className="group flex flex-col gap-1.5 py-3 transition-colors sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium whitespace-nowrap text-primary-800">
                    <i className="ri-file-search-line" />
                    {r.tag}
                  </span>
                  <span className="min-w-0 flex-1 text-sm text-foreground-800 group-hover:text-primary-700">
                    {r.title}
                  </span>
                  <span className="shrink-0 text-xs whitespace-nowrap text-foreground-500">
                    {r.category} · {r.time ?? r.views}
                  </span>
                </a>
              </li>
            ))}
            {rows.length === 0 && (
              <li className="py-10 text-center text-sm text-foreground-500">
                没有找到匹配的素材，试试切换筛选或更换关键词。
              </li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
}